import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { TrackingEvents } from '../../../_shared/data/tracking-events.data';
import { LinkDirective } from '../../../_shared/directives/link.directive';
import { EMPTY_LINK, ILink } from '../../../_shared/models/link.model';
import { IMenuGroup } from '../../../_shared/models/navigation/menu/menu-group.model';
import { ISubMenuHighlight } from '../../../_shared/models/navigation/menu/sub-menu-highlight.model';
import { ISubMenuItem } from '../../../_shared/models/navigation/menu/sub-menu-item.model';
import { GtmService } from '../../../_shared/services/gtm.service';
import { LinkService } from '../../../_shared/services/link.service';
import { SnowplowService } from '../../../_shared/services/snowplow.service';

@Component({
    selector: 'sirius-navigation-menu-dropdown',
    templateUrl: './navigation-menu-dropdown.component.html',
    styleUrls: ['./navigation-menu-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'sir-text-style-paragraph-m-default' },
    imports: [LinkDirective],
})
export class NavigationMenuDropdownComponent {
    private readonly _gtmService = inject(GtmService);
    private readonly _snowplowService = inject(SnowplowService);
    private readonly _linkService = inject(LinkService);

    readonly content = input<IMenuGroup | undefined>(undefined);

    readonly title = computed<string>(() => this.content()?.title ?? '');
    readonly link = computed<ILink>(() => this.content()?.link ?? EMPTY_LINK);
    readonly columns = computed<ISubMenuItem[][]>(() => this.content()?.columns ?? []);
    readonly highlightItems = computed<ISubMenuHighlight[] | undefined>(() => this.content()?.highlightItems);

    readonly clickEvent = output<void>();

    onClick(link: ILink | undefined, event: Event): void {
        this.addTracking(link, event.target as HTMLElement);
        this.clickEvent.emit();
    }

    //#region tracking
    private addTracking(link: ILink | undefined, anchor: HTMLElement): void {
        const data = this._linkService.getTrackingData(anchor);
        const cfg = {
            ...link?.tracking?.parameters,
            link_classes: data.linkClasses,
            link_id: data.linkId,
            link_text: data.linkText,
            link_url: data.linkUrl,
            outbound: data.outbound,
        };

        this._gtmService.track(TrackingEvents.gtm['topMenuClick']([cfg]));

        const cfg2 = {
            identifier: link?.tracking?.parameters['identifier'],
            category: 'menu',
            element_classes: data.linkClasses.split(' '),
            target_url: data.linkUrl,
            element_content: data.linkText,
            outbound: data.outbound !== 'FALSE',
            context: [],
        };

        this._snowplowService.track('linkClick', cfg2);
    }
    //#endregion
}
